import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { ADD_ASSESSMENT_OPTION } from "graphql/mutations";
import { H3 } from "components/Text";
import { EditLink } from "components/Links";
import { Header } from "components/DisplayBox";
import { EditButton } from "components/Buttons";
import { SIZE, TYPE, Toggle } from "components/Form";
import { margins } from "style";
import { MODAL_SIZE, ModalForm } from "components/Modal";
import { useToast, useAuth, useMissionRole } from "hooks";
import { ROLES_TEMPLATE_VARIABLES } from "constants/index";
import { formatInterpolatedText } from "utils";
import { DataTableProvider } from "components/DataTable";
import { TOGGLE_SIZE } from "components/Form/Toggle";

/**
 * RoleRequirements
 *
 * @param {Object}     missionRole
 * @param {Function}   refetch
 * @param {Boolean}    loading
 * @param {Boolean}    canEdit        Defaults to admin-only; pass it where another role may manage the vetting
 * @param {Boolean}    canRemove      Whether requirements may be deleted outright, not only switched off
 * @param {Array}      refetchQueries Queries to re-read after a requirement is deleted, for a caller the cache cannot update
 */
const RoleRequirements = ({
  missionRole = {},
  refetch,
  loading,
  canEdit: canManage,
  canRemove: canRemoveProp = false,
  refetchQueries = [],
  ...props
}) => {
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();
  const { isAdmin, isTalentSimpleClient } = useAuth();
  const canManageRequirements = canManage ?? isAdmin;

  const handleClose = () => {
    setShowForm(false);
  };

  const handleComplete = () => {
    handleClose();
    toast.success(<FormattedMessage id="RoleRequirements.AddRequirementForm.Success" />);
  };

  const {
    id: missionRoleId,
    requirements = [],
    availableRequirements = [],
    requirementsLocked: isLocked,
    roleRequirementsQuestion,
  } = missionRole;

  const hasAvailableOptions = availableRequirements.length > 0;
  const canEdit = !isLocked && canManageRequirements && !loading;
  const canRemove = canEdit && canRemoveProp;

  // Get selected requirement IDs
  const selectedIds = useMemo(() => requirements.map((req) => req.id), [requirements]);

  // For ADMINS: formatted available requirements (all options with selection state)
  const formattedAvailableRequirements = useMemo(
    () =>
      availableRequirements.map((req) => ({
        id: req.id,
        value: req.value, // Plain string value for mutation
        description: canManageRequirements ? formatInterpolatedText(req.originalValue, req.value) : req.value,
        originalValue: req.originalValue,
        isSelected: selectedIds.includes(req.id),
      })),
    [availableRequirements, selectedIds, canManageRequirements]
  );

  // For NON-ADMINS: formatted selected requirements only
  const formattedRequirements = useMemo(
    () =>
      requirements.map((req) => ({
        id: req.id,
        value: req.description,
        description: canManageRequirements
          ? formatInterpolatedText(req.originalValue, req.description)
          : req.description,
        originalValue: req.originalValue,
        isSelected: true, // Always true since these are the selected ones
      })),
    [requirements, canManageRequirements]
  );

  const {
    handleUpdateRequirements,
    updatingRequirements: updating,
    removingRequirement: removing,
    getRequirementRowOptions,
  } = useMissionRole({
    id: missionRoleId,
    skip: true,
    refetchQueries,
  });

  const handleCheckboxChange = (requirementId, checked) => {
    if (!canEdit || updating) return;

    let newSelectedIds;
    if (checked) {
      newSelectedIds = [...selectedIds, requirementId];
    } else {
      newSelectedIds = selectedIds.filter((id) => id !== requirementId);
    }

    // Find the requirement objects for the selected IDs - use plain string value, not JSX description
    const selectedRequirements = newSelectedIds
      .map((id) => {
        const req = formattedAvailableRequirements.find((r) => r.id === id);
        return req ? { id, value: req.value } : null;
      })
      .filter(Boolean);

    handleUpdateRequirements(selectedRequirements);
  };

  return (
    <>
      <Header {...props}>
        <H3>
          <FormattedMessage id={`Missions.MissionRole.${isTalentSimpleClient ? "Employment" : "Role"}Requirements`} />
        </H3>
        {canEdit && hasAvailableOptions && (
          <EditLink onClick={() => setShowForm(true)}>
            <FormattedMessage id="RoleRequirements.AddButtonText" />
          </EditLink>
        )}
      </Header>
      {(!canEdit || hasAvailableOptions) && (
        <DataTableProvider
          data={canEdit ? formattedAvailableRequirements : formattedRequirements}
          columns={COLUMN_DATA(canEdit, loading || updating || removing, handleCheckboxChange)}
          rowOptions={canRemove ? getRequirementRowOptions : null}
          loading={loading || updating || removing}
          disablePagination
          marginSize={margins.normal}
          emptyMessage={{ title: "Missions.Applicants.RoleRequirements.EmptyMsg" }}
          hideHeader
        />
      )}
      {canEdit && !hasAvailableOptions && !isLocked && (
        <EditButton onClick={() => setShowForm(true)}>
          <FormattedMessage id="RoleRequirements.AddButtonText" />
        </EditButton>
      )}
      {showForm && (
        <ModalForm
          data={FORM_DATA(roleRequirementsQuestion?.id, canManageRequirements, refetch, handleComplete)}
          show={showForm}
          onClose={handleClose}
          size={MODAL_SIZE.medium}
          numColumns={3}
          title={<FormattedMessage id="RoleRequirements.AddRequirementForm.Title" />}
          showSave={false}
        />
      )}
    </>
  );
};

const COLUMN_DATA = (canEdit, loading, handleCheckboxChange) => [
  {
    cell: (rowData) => (
      <Toggle
        name={`requirement-${rowData.id}`}
        value={rowData.isSelected}
        onChange={(_, checked) => handleCheckboxChange(rowData.id, checked)}
        disabled={!canEdit || loading}
        size={TOGGLE_SIZE.small}
      />
    ),
    width: "60px",
  },
  {
    cell: ({ description }) => description,
    width: "auto",
  },
];

const FORM_DATA = (questionId, canManageRequirements, refetch, handleComplete) => [
  {
    items: [
      canManageRequirements &&
        questionId && {
          type: TYPE.createNewInstance,
          size: SIZE.xlarge,
          properties: {
            name: "requirements",
            labelKey: "Profile.CreateNewRequirement",
            placeholder: "Type requirement...",
            queryName: ADD_ASSESSMENT_OPTION,
            keyName: "value",
            fields: [
              {
                type: TYPE.input,
                name: "value",
                properties: {
                  required: true,
                },
              },
            ],
            variables: { id: questionId },
            refetch,
            templateVariables: ROLES_TEMPLATE_VARIABLES,
            initiallyExpanded: true,
            onComplete: handleComplete,
          },
        },
    ].filter(Boolean),
  },
];

RoleRequirements.propTypes = {
  missionRole: PropTypes.shape({
    id: PropTypes.string.isRequired,
    requirementsLocked: PropTypes.bool,
    requirements: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      })
    ),
    availableRequirements: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
  }),
  refetch: PropTypes.func,
  loading: PropTypes.bool,
  canEdit: PropTypes.bool,
  canRemove: PropTypes.bool,
  refetchQueries: PropTypes.array,
};

export default RoleRequirements;
